/**
 * workspaceHub/completeness.ts
 * Qué hace: mide cuánta información tenemos de una marca y devuelve qué falta,
 *   con el link para completarlo. Es la fuente del "%" y de las alertas del hub.
 * Qué exporta: buildChecklist(), ChecklistItem, Checklist.
 * Quién lo usa: pages/WorkspaceHubPage.tsx.
 *
 * Criterio: "required" es lo que la app NECESITA para producir un ad decente
 * (sin logo no hay template branded; sin sitio no hay research; sin contacto el
 * poster sale sin forma de contactar). El resto suma calidad pero no bloquea.
 * No hay campo CTA a nivel marca — lo más cercano es el contacto, que es lo que
 * efectivamente se estampa en los ads.
 */
import { BrandInfo, Workspace } from '@agency-smm/shared';

export interface ChecklistItem {
  key: string;
  label: string;
  /** Por qué le importa al usuario. Se muestra en las alertas. */
  hint: string;
  done: boolean;
  required: boolean;
  /** Ruta relativa al workspace donde se completa ESE dato, con la pestaña ya
   *  seleccionada (la página de marca acepta ?tab=). Sin esto, el checklist te
   *  deja en la puerta y tenés que buscar el campo a mano. */
  fixPath: string;
}

export interface Checklist {
  items: ChecklistItem[];
  percent: number;
  missingRequired: ChecklistItem[];
  missingOptional: ChecklistItem[];
}

const hasText = (v?: string | null): boolean => !!v && v.trim().length > 0;
const hasList = (v?: string[] | null): boolean => Array.isArray(v) && v.length > 0;

export function buildChecklist(
  workspace: Pick<Workspace, 'logoUrl' | 'assetsCount'>,
  brand: BrandInfo | null
): Checklist {
  const voice = brand?.voiceGuide;
  const voiceFilled = !!voice && [
    voice.purpose, voice.audience, voice.tone, voice.emotionalTone,
    voice.character, voice.syntax, voice.language,
  ].some(hasText);

  const items: ChecklistItem[] = [
    {
      key: 'logo',
      label: 'Brand logo',
      hint: 'Without it, branded templates fall back to plain text.',
      done: hasText(workspace.logoUrl),
      required: true,
      fixPath: 'brand?tab=visual',
    },
    {
      key: 'website',
      label: 'Website',
      hint: 'It is what brand research and AI Visibility scan.',
      done: hasText(brand?.sourceUrl),
      required: true,
      fixPath: 'brand?tab=sources',
    },
    {
      key: 'overview',
      label: 'What the business does',
      hint: 'Every post is written from this. Thin here means generic copy.',
      done: hasText(brand?.businessOverview) || hasText(brand?.description),
      required: true,
      fixPath: 'brand?tab=identity',
    },
    {
      key: 'audience',
      label: 'Target audience',
      hint: 'Decides who the copy talks to.',
      done: hasText(brand?.targetAudience),
      required: true,
      fixPath: 'brand?tab=identity',
    },
    {
      key: 'contact',
      label: 'Contact info',
      hint: 'Phone, WhatsApp or email — stamped on ads as real data.',
      done: hasText(brand?.phoneNumber) || hasText(brand?.whatsapp) || hasText(brand?.email),
      required: true,
      fixPath: 'brand?tab=identity',
    },
    {
      key: 'voice',
      label: 'Brand voice',
      hint: 'Tone, character and language of the copy.',
      done: voiceFilled || hasText(brand?.tone),
      required: false,
      fixPath: 'brand?tab=voice',
    },
    {
      key: 'keywords',
      label: 'Keywords',
      hint: 'Feed image search and SEO wording.',
      done: hasList(brand?.keywords),
      required: false,
      fixPath: 'brand?tab=voice',
    },
    {
      key: 'topics',
      label: 'Post topics',
      hint: 'Without them the AI picks the subjects on its own.',
      done: hasList(brand?.postTopics),
      required: false,
      fixPath: 'brand?tab=voice',
    },
    {
      key: 'products',
      label: 'Products & services',
      hint: 'What the posts can actually promote.',
      done: hasList(brand?.productsServices),
      required: false,
      fixPath: 'brand?tab=identity',
    },
    {
      key: 'colors',
      label: 'Brand colors',
      hint: 'Templates use them instead of the default palette.',
      done: hasList(brand?.brandColors),
      required: false,
      fixPath: 'brand?tab=visual',
    },
    {
      key: 'assets',
      label: 'Brand assets',
      hint: 'Real photos beat stock images in every ad.',
      done: (workspace.assetsCount || 0) > 0,
      required: false,
      fixPath: 'brand?tab=assets',
    },
  ];

  const done = items.filter((i) => i.done).length;

  return {
    items,
    percent: Math.round((done / items.length) * 100),
    missingRequired: items.filter((i) => !i.done && i.required),
    missingOptional: items.filter((i) => !i.done && !i.required),
  };
}
